import React, { useCallback } from 'react';
import { Button, Tooltip } from 'antd';
import Loader from '@components/Loader';
import { useDispatch } from 'react-redux';
import { AWAssetDto } from '@api/Api';
import { toggleBillingModal } from '@redux/actions/modalAction';
import { useOrganization } from '@components/OrganizationBoundary';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { ReactComponent as CrossXsSvg } from '@assets/icons/cross-xs.svg';
import { ReactComponent as StartFolderSvg } from '@assets/icons/start-folder.svg';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import ApprovalStagesUpgradeImg from '@assets/images/approval-routing-stages-upgrade.png';

import './AssetsPreview.less';

export type AssetsPreviewProps = {
  companyName: string;
  selectedAssetVersions: AWAssetDto[];
  setSelectedAssetVersions: (value: AWAssetDto[]) => void;
};

function ApprovalAssetItem({
  asset,
  onDeleteAsset
}: {
  asset: AWAssetDto;
  onDeleteAsset: (asset: AWAssetDto) => void;
}) {
  const preview = useCallback(() => {
    return assetPreviewThumbnails({
      url: asset.previewUrl || '',
      status: 'ready',
      type: asset.type,
      size: 40,
      format: 'svg'
    });
  }, [asset.previewUrl, asset.type])();

  return (
    <div className="assets-preview_item">
      <div className="assets-preview_item-img">
        {preview === 'pending' && <Loader spinSize={32} isSpinning={true} />}
        {preview !== 'pending' ? (
          typeof preview === 'string' ? (
            <img src={preview || ''} alt="" />
          ) : (
            preview
          )
        ) : null}
      </div>
      <div className="assets-preview_item-info">
        <Tooltip
          title={`${asset.name}.${asset.extension}`}
          overlayClassName="approval-modal-tooltip"
          className="assets-preview_item-name"
        >
          {asset.name}
        </Tooltip>
        <div className="assets-preview_item-extension">.{asset.extension}</div>
      </div>
      <div className="assets-preview_item-version">v{asset.versionsCount}</div>
      <div
        onClick={() => onDeleteAsset(asset)}
        className="assets-preview_item-action"
      >
        <CrossXsSvg />
      </div>
    </div>
  );
}

export default function AssetsPreview({
  companyName,
  selectedAssetVersions,
  setSelectedAssetVersions
}: AssetsPreviewProps) {
  const dispatch = useDispatch();
  const { currentOrganization } = useOrganization();
  const canUseBatchApprovals =
    !!currentOrganization.entity?.features.batchApprovals;

  const onDeleteAsset = useCallback(
    (asset: AWAssetDto) => {
      setSelectedAssetVersions(
        selectedAssetVersions.filter((item) => item.id !== asset.id)
      );
    },
    [selectedAssetVersions, setSelectedAssetVersions]
  );

  return (
    <div className="assets-preview">
      <Tooltip
        title={`Selected media ${
          companyName ? `from ${companyName} project` : ''
        }`}
        placement="topLeft"
        overlayClassName="approval-modal-tooltip"
        className="media-step_title"
        overlayInnerStyle={{
          maxWidth: 450
        }}
      >
        Selected media {companyName ? `from ${companyName} project` : ''}
      </Tooltip>
      <div className="assets-preview_content">
        {!selectedAssetVersions.length && (
          <div className="assets-preview_empty-state">
            <StartFolderSvg />
            <p className="main-body-text main-body-text--dark-gray">
              Your selected media will appear here
            </p>
          </div>
        )}
        {!!selectedAssetVersions?.length &&
          selectedAssetVersions.map((item) => (
            <ApprovalAssetItem
              asset={item}
              key={item.id}
              onDeleteAsset={onDeleteAsset}
            />
          ))}
        {!!selectedAssetVersions?.length && !canUseBatchApprovals && (
          <div className="assets-preview_upgrade">
            <div className="assets-preview_upgrade-image">
              <img src={ApprovalStagesUpgradeImg} alt="Upgrade" />
            </div>
            <div className="assets-preview_upgrade-content">
              <div className="assets-preview_upgrade-title">
                Want to create bulk approval?
              </div>
              <div className="assets-preview_upgrade-text">
                Update your plan to select
                <br /> multiple assets for approval
              </div>
            </div>
            <Button
              className="assets-preview_upgrade-btn"
              type="primary"
              size="small"
              icon={<UpgradePlanSvg />}
              onClick={() => dispatch(toggleBillingModal({ visible: true }))}
            >
              Upgrade
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
